import { isNil } from 'lodash';

import { RECONCILIATION_DECISIONS_CACHE } from './cacheKeys';

const MAX_TRACKED_DECISIONS = 200;

type PendingDecisions = Record<string, string>;

const decisionsInMemory = new Map<string, string>();

const readPersistedDecisions = (): PendingDecisions => {
  try {
    const stored = window.localStorage.getItem(RECONCILIATION_DECISIONS_CACHE);
    if (isNil(stored)) {
      return {};
    }
    const parsed = JSON.parse(stored);
    if (isNil(parsed) || Array.isArray(parsed) || typeof parsed !== 'object') {
      return {};
    }
    return Object.entries(parsed).reduce<PendingDecisions>((acc, [attemptKey, decisionId]) => {
      if (typeof decisionId === 'string') {
        acc[attemptKey] = decisionId;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const writePersistedDecisions = (pendingDecisions: PendingDecisions) => {
  try {
    window.localStorage.setItem(RECONCILIATION_DECISIONS_CACHE, JSON.stringify(pendingDecisions));
  } catch {
    // Storage unavailable, the memory tier still holds the id
  }
};

const withoutOldestDecisions = (pendingDecisions: PendingDecisions): PendingDecisions => {
  const attemptKeys = Object.keys(pendingDecisions);
  if (attemptKeys.length <= MAX_TRACKED_DECISIONS) {
    return pendingDecisions;
  }
  return attemptKeys.slice(-MAX_TRACKED_DECISIONS).reduce<PendingDecisions>((acc, attemptKey) => {
    const decisionId = pendingDecisions[attemptKey];
    if (!isNil(decisionId)) {
      acc[attemptKey] = decisionId;
    }
    return acc;
  }, {});
};

const forgetOldestDecisionsInMemory = () => {
  Array.from(decisionsInMemory.keys())
    .slice(0, Math.max(decisionsInMemory.size - MAX_TRACKED_DECISIONS, 0))
    .forEach((attemptKey) => decisionsInMemory.delete(attemptKey));
};

export const readPendingDecisionId = (attemptKey: string): string | undefined =>
  decisionsInMemory.get(attemptKey) ?? readPersistedDecisions()[attemptKey];

export const storePendingDecisionId = (attemptKey: string, decisionId: string) => {
  decisionsInMemory.set(attemptKey, decisionId);
  forgetOldestDecisionsInMemory();
  writePersistedDecisions(withoutOldestDecisions({ ...readPersistedDecisions(), [attemptKey]: decisionId }));
};

export const forgetPendingDecisionId = (attemptKey: string) => {
  decisionsInMemory.delete(attemptKey);
  const pendingDecisions = readPersistedDecisions();
  if (!(attemptKey in pendingDecisions)) {
    return;
  }
  delete pendingDecisions[attemptKey];
  writePersistedDecisions(pendingDecisions);
};
